// --- React & Redux
import React from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Col, Modal, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
// --- API & Utils
import { datastageApi } from 'datastage/api.js';
import { getItemRange } from 'datastage/utils/pagination';
import { fetchReadyProductsPage } from 'datastage/pages/Products/utils/fetchReadyProductsPage.js';
// --- Hooks & Store
import { useReadyProductsActions } from 'datastage/store/readyProducts/useReadyProducts.js';
import { useLoading } from 'datastage/utils/hooks/useLoading.js';
import { useToasts } from 'datastage/utils/hooks/useToasts.js';
// --- Components
import { DetailsModal } from 'datastage/components/DetailsModal/index.jsx';
import { Pagination } from 'datastage/components/Pagination/index.jsx';
import { ReadyProductsGrid } from 'datastage/pages/Products/components/modal/grid/index.jsx';

export const ReadyProductsModal = () => {
    const {
        isModalOpen,
        totalPages,
        currentPage,
        itemsPerPage,
        totalItems,
        readyProductRange,
        readyProductSort,
    } = useSelector((state) => state.readyProducts);
    const { clearReadyProducts, updateReadyProducts } = useReadyProductsActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast, showSuccessToast } = useToasts();

    const onCloseModal = () => {
        clearReadyProducts();
    };

    const handleChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        setLoadingTrue();
        try {
            const data = await fetchReadyProductsPage(nextPageRange, readyProductSort);
            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch ready products.');
                return;
            }
            if (data) {
                updateReadyProducts({
                    readyProducts: data?.content ?? [],
                    totalPages: data?.totalPages ?? 0,
                    totalItems: data?.totalElements ?? 0,
                    currentPage: data?.pageable?.pageNumber ?? 0,
                    readyProductRange: nextPageRange,
                });
            }
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch ready products.');
            console.error('Error fetching ready products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleRefresh = async () => {
        setLoadingTrue();
        try {
            const data = await fetchReadyProductsPage(readyProductRange, readyProductSort);
            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch ready products.');
                return;
            }
            if (data) {
                updateReadyProducts({
                    readyProducts: data?.content ?? [],
                    totalItems: data?.totalElements ?? 0,
                    totalPages: data?.totalPages ?? 0,
                    currentPage: data?.pageable?.pageNumber ?? 0,
                });
            }
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch ready products.');
            console.error('Error fetching ready products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handlePushClick = () => {
        Modal.confirm({
            title: 'Push Ready Products',
            content:
                'Are you sure you want to push all ready products? This action can not be reversed.',
            okText: 'Yes, Push Product(s)',
            cancelText: 'Cancel',
            onOk: async () => {
                setLoadingTrue();
                try {
                    const result = await datastageApi.pushReadyProducts();
                    if (
                        result != null &&
                        typeof result === 'object' &&
                        (result.code != null || result.error != null)
                    ) {
                        showErrorToast(
                            result.message || result.error || 'Failed to push ready products.',
                        );
                        return;
                    }
                    showSuccessToast('Job created to push ready products.');
                    clearReadyProducts();
                } catch (error) {
                    showErrorToast(error?.message || 'Failed to push ready products.');
                } finally {
                    setLoadingFalse();
                }
            },
        });
    };

    return (
        <DetailsModal
            modalHeader={
                <Row style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Col style={{ flex: 1, marginTop: 10 }}>
                        <Typography variant="T40">Push Ready Products</Typography>
                    </Col>
                    <Col
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginTop: 15,
                            marginRight: 25,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            iconType="fa-solid fa-rotate"
                            labelText="Refresh"
                            styleType="link"
                            onClick={handleRefresh}
                        >
                            Refresh
                        </Button>
                        <Button
                            iconType="fa-solid fa-share"
                            labelText="Push Product(s)"
                            styleType="link"
                            onClick={handlePushClick}
                            disabled={totalItems === 0}
                        >
                            Push Product(s)
                        </Button>
                    </Col>
                </Row>
            }
            isOpen={isModalOpen}
            onClose={onCloseModal}
            footer={
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Pagination
                        pageCount={totalPages}
                        onPageChange={(e) => handleChange(e.selected)}
                        previousLabel="←"
                        nextLabel="→"
                        forcePage={currentPage}
                    />
                </div>
            }
        >
            <ReadyProductsGrid />
        </DetailsModal>
    );
};
